import { useEffect, useRef } from "react";
import { Button as AriaButton } from "react-aria-components";

import closeIcon from "@/assets/figma/store-overview/close.svg";
import collapseIcon from "@/assets/figma/store-overview/collapse.svg";
import newChatIcon from "@/assets/figma/store-overview/new-chat.svg";
import { WorkspaceConversation } from "@/features/chat/WorkspaceConversation";
import { useChatAttachmentUploads } from "@/features/chat/useChatAttachmentUploads";
import {
  selectWorkspaceResourceCapabilities,
} from "@/features/chat/resourceAvailability";
import { useGetResourceAvailabilityQuery } from "@/features/chat/resourceAvailabilityApi";

interface TemporaryStoreOverviewChatProps {
  conversationId: string;
  isClosing: boolean;
  onClose: () => void;
  onCollapse: () => void;
  organizationId: string;
  userId: string;
}

const ignorePendingSend = () => undefined;
const ignoreStrategyAdjustment = () => undefined;

export function TemporaryStoreOverviewChat({
  conversationId,
  isClosing,
  onClose,
  onCollapse,
  organizationId,
  userId,
}: TemporaryStoreOverviewChatProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const resourceAvailabilityQuery = useGetResourceAvailabilityQuery(organizationId);
  const resourceCapabilities = selectWorkspaceResourceCapabilities(
    resourceAvailabilityQuery.data,
  );
  const uploads = useChatAttachmentUploads(organizationId, conversationId, {
    isUploadDisabled: !resourceCapabilities.canUploadNewFiles,
  });

  useEffect(() => {
    titleRef.current?.focus();
  }, [conversationId]);

  return (
    <section
      aria-labelledby="temporary-store-overview-chat-title"
      className="temporary-store-overview-chat"
      data-figma-node-id="738:114645"
      data-testid="temporary-store-overview-chat"
    >
      <header className="temporary-store-overview-chat__header">
        <span aria-hidden="true" className="temporary-store-overview-chat__brand">
          <img alt="" src={newChatIcon} />
        </span>
        <div>
          <h2 id="temporary-store-overview-chat-title" ref={titleRef} tabIndex={-1}>
            临时聊天
          </h2>
          <p>全店语境中不执行单店动作</p>
        </div>
        <div className="temporary-store-overview-chat__actions">
          <AriaButton
            aria-label="收起临时聊天"
            className="store-overview-icon-button"
            isDisabled={isClosing}
            onPress={onCollapse}
          >
            <img alt="" src={collapseIcon} />
          </AriaButton>
          <AriaButton
            aria-label="关闭并删除临时聊天"
            className="store-overview-icon-button"
            isDisabled={isClosing}
            onPress={onClose}
          >
            <img alt="" src={closeIcon} />
          </AriaButton>
        </div>
      </header>

      <div className="temporary-store-overview-chat__notice" role="note">
        这段对话绑定当前全店快照；关闭后永久删除。需要执行店铺动作时，请回到正式对话并重新选择店铺。
      </div>

      <div className="temporary-store-overview-chat__runtime">
        <WorkspaceConversation
          attachments={uploads.attachments}
          conversationId={conversationId}
          isUploadDisabled={!resourceCapabilities.canUploadNewFiles}
          onAttachmentsChange={uploads.replaceAttachments}
          onPendingSendConsumed={ignorePendingSend}
          onReset={onClose}
          onRetryAttachment={uploads.retryAttachment}
          onStrategyAdjustmentConsumed={ignoreStrategyAdjustment}
          onUploadFiles={uploads.acceptFiles}
          organizationId={organizationId}
          pendingSend={null}
          presentation="temporary-store-overview"
          strategyAdjustment={null}
          targetSequence={null}
          userId={userId}
        />
      </div>

      {isClosing ? (
        <div aria-live="polite" className="temporary-store-overview-chat__closing">
          正在停止运行并删除临时聊天…
        </div>
      ) : null}
    </section>
  );
}
